import * as d3 from "d3";

import { useEffect, useRef, useCallback, useState } from "react";
import RoleInfo from "../../components/template/base-template/roleInfo/role-info";
import { useDispatch, useSelector } from "react-redux";
import { saveRelatedState, setFilterRole, setRoleInfo, stateRelatedRoles } from "../../store/ducks/relatedRoles";
import D3Chart, { gerarNovaCoordenada, zoom } from "../../libs/d3jsmap/d3jsmap";
import "./workbanck.scss";
import { mouseEnter, mouseLeave } from "../../libs/d3jsmap/hook";
import { colorsMaps } from "../../constants/colorsArea";
import { api } from "../../services";
import { isMobile, widthAndHeigth } from "../../utils/screenValidations/screen";
import { roleSelected } from "../../utils/roleSelected";
import Role from "../../utils/interfaces/IRole";
import { useSearchParams } from "react-router-dom";

const Workbench = () => {
    const chartArea = useRef<any>(null);
    const [focusableNode, setFocusableNode] = useState(0);
    const { selectedRole, relatedRole, filterRole } = useSelector(stateRelatedRoles);
    const dispatch = useDispatch();
    const [params] = useSearchParams();
    const getRoleByNodeId = useCallback(
        async (node: Role) => {
            mouseEnter(node);
            const response = await api.get(`roles/${node.role_id}?limit=500&responseType=stream`);
            dispatch(setRoleInfo(response.data));
        },
        [dispatch]
    );

    const callApi = useCallback(async () => {
        try {
            if (relatedRole?.nodes.length === 0) {
                const response = await api.get("/roles?limit=500&responseType=stream");
                dispatch(saveRelatedState(sanitizeData(response.data || []) || []));
            }
        } catch (error) {
            console.error("Error while loading role data:", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, relatedRole?.nodes.length]);

    useEffect(() => {
        callApi();
    }, [callApi]);

    const setZoom = (value = 1.5) => {
        const container = d3.select(chartArea.current);
        zoom.scaleBy(container.transition().duration(300), value);
    };

    const zoomOut = () => {
        const container = d3.select(chartArea.current);
        const containernodes = d3.select("g.container-nodes");
        const valueScale = containernodes.attr("transform")?.match(/scale\((\d+(\.\d+)?)\)/)?.[1];
        if (valueScale) {
            zoom.scaleTo(container.transition().duration(300), Number(valueScale) - 0.5);
        }
    };

    const handleShowRoleInfo = (value: boolean) => {
        if (!value) {
            dispatch(setRoleInfo(null));
            roleSelected().forEach((el) => el?.classList.remove("clicked-node"));
            mouseLeave();
        }
    };

    function sanitizeData(data: Role[]) {
        if (relatedRole?.nodes.length) return;
        const width = isMobile() ? widthAndHeigth().width * 4 : widthAndHeigth().width + 100;
        const heigth = isMobile() ? widthAndHeigth().height * 2 : widthAndHeigth().height + 200;
        let links: any = [];
        const nodes: Role[] = [];
        data.forEach((related: Role) => {
            const generateCoords = gerarNovaCoordenada(nodes, 35, width, heigth, related);

            nodes.push({
                ...related,
                ...generateCoords,
                color: colorsMaps.find((elColor: any) => elColor.trajectory_id === related.trajectory_id)?.color,
                trajectory: {
                    trajectory: colorsMaps.find((elColor: any) => elColor.trajectory_id === related.trajectory_id)?.trajectory || "",
                    trajectory_id: related.trajectory_id,
                },
            });
        });
        nodes.forEach((related: Role) => {
            related.related_roles.forEach((relatedRole: any) => {
                const findNodes = nodes.find((node: any) => node.role_id == relatedRole.role_id);

                if (findNodes) {
                    var dx = findNodes.x - related.x;
                    var dy = findNodes.y - related.y;
                    var dr = Math.sqrt(dx * dx + dy * dy);
                    var offsetX = (dx * 30) / dr;
                    var offsetY = (dy * 30) / dr;

                    var offsetXSource = (dx * 30) / dr;
                    var offsetYSource = (dy * 30) / dr;
                    links.push({
                        source: {
                            id: related.role_id,
                            x: related.x + offsetXSource,
                            y: related.y + offsetYSource,
                            colorLine: related.color,
                        },
                        target: {
                            idRelated: relatedRole.role_id,
                            x: findNodes.x - offsetX,
                            y: findNodes.y - offsetY,
                        },
                    });
                }
            });
        });

        return {
            links,
            nodes,
        };
    }

    //show map
    const onMouseLeave = useCallback(() => {
        setFocusableNode(0);
        const focusableNodes = roleSelected().length;
        if (!focusableNodes) mouseLeave();
    }, []);

    const drawLines = useCallback(
        (node: Role) => {
            if (focusableNode === node.role_id) return;
            setFocusableNode(node.role_id);

            const containernodes = d3.select("g.container-nodes");

            const links = relatedRole.links.filter((element) => {
                const displayed = document?.querySelector(`#idNodes${element.target.idRelated}`)?.getAttribute("style");
                return element.source.id === node.role_id && !displayed?.includes?.("none");
            });
            if (links.length === 0) return;
            containernodes
                .append("g")
                .attr("class", "links linkActive")
                .selectAll("line")
                .data(links)
                .enter()
                .append("line")
                .attr("id", ({ source }) => `link${source.id}`)
                .attr("x1", (d) => d.source.x)
                .attr("y1", (d) => d.source.y)
                .attr("x2", (d) => d.target.x)
                .attr("y2", (d) => d.target.y)
                .attr("style", (d) => `stroke:${d.source.colorLine}`);
        },
        [focusableNode, relatedRole.links]
    );

    const onMouseEnter = useCallback(
        (node: Role, isClicked = false) => {
            const focusableNodes = roleSelected().length;
            if (!focusableNodes || isClicked) {
                mouseEnter(node);
                drawLines(node);
            }
        },
        [drawLines]
    );

    const clicked = useCallback((node: Role) => {
        const svgContainer = d3.select(chartArea.current);
        var h = widthAndHeigth().height,
            w = widthAndHeigth().width;

        var scale = 0.5;
        var translate = [(w - node!.x) / 2, (h - node!.y) / 2];

        svgContainer.transition().duration(500).call(zoom.transform, d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale));

        setTimeout(() => {
            setZoom(isMobile() ? 4 : 6);
        }, 500);
        // }
    }, []);

    const onCLickNode = useCallback(
        (node: Role, isFiltered = true) => {
            mouseLeave();

            roleSelected().forEach((el) => el?.classList.remove("clicked-node"));
            document?.querySelector(`#idNodes${node.role_id}`)?.classList.add("clicked-node");
            clicked(node);
            if (isFiltered) {
                dispatch(setFilterRole(null));
            }

            onMouseEnter(node, true);
            getRoleByNodeId(node);
        },
        [clicked, dispatch, getRoleByNodeId, onMouseEnter]
    );

    useEffect(() => {
        if (relatedRole.nodes.length > 0) {
            const svgContainer = d3.select(chartArea.current);
            D3Chart(svgContainer, relatedRole, onCLickNode, onMouseEnter, onMouseLeave);
        }
    }, [onCLickNode, onMouseEnter, onMouseLeave, relatedRole]);

    // show node when filter by text or click in glossary
    useEffect(() => {
        if (filterRole && (document.querySelector("#svg-map") || document.querySelector('[opensidebar="false"]'))) {
            onCLickNode(filterRole, false);
        }
    }, [filterRole]);

    useEffect(() => {
        const svgContainer = d3.select(chartArea.current);

        if (isMobile() && relatedRole.nodes.length) {
            svgContainer
                .transition()
                .duration(500)
                .call(zoom.transform, d3.zoomIdentity.translate(-(widthAndHeigth().width / 2), 0).scale(0.4));
        }
    }, [relatedRole.nodes.length]);

    return (
        <>
            <svg
                ref={chartArea}
                id="svg-map"
                style={{
                    userSelect: "none",
                }}
            ></svg>

            <RoleInfo
                zoomIn={setZoom}
                zoomOut={zoomOut}
                setShowRoleInfo={(value) => {
                    handleShowRoleInfo(value);
                }}
                selectedRole={selectedRole}
                showRoleInfo={selectedRole}
            />
        </>
    );
};

export default Workbench;
